// Chi tiết công việc: thông tin, thao tác theo vai trò, việc con, lịch sử & bình luận
import React, { useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Drawer, Dropdown, Empty, Input, Modal, Select, Timeline, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Camera, MoreHorizontal, Plus, Send, X } from 'lucide-react';
import { ME_ID, REPEAT_LABEL, memberName, projectOf, type Repeat, type WorkTask } from './data';
import { ACTION_LABEL, ACTIVITY_TEXT, allowedActions, canCheck, isClosed, isOverdue, submitBlocker, type Action } from './flow';
import useWorkStore from './store';
import TaskForm, { memberOptions, repeatOptions } from './TaskForm';
import { Avatar, CheckCount, DueText, Field, PriorityTag, RepeatTag, StatusTag, compressImage, fmtDate } from './ui';
import { bucketOf } from '../WorkReport/logic';

const PRIMARY: Action[] = ['start', 'submit', 'approve', 'return'];
const NEEDS_NOTE: Action[] = ['return', 'due', 'fail', 'cancel'];
const MAX_PHOTOS = 4;

function ActionModal({ task, action, onClose }: { task: WorkTask; action: Action | null; onClose: () => void }) {
  const act = useWorkStore(s => s.act);
  const [note, setNote] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | undefined>();
  const [due, setDue] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<string[]>([]);
  if (!action) return null;
  const ok = () => {
    let err: string | null;
    try {
      err = act(task.id, action, { by: ME_ID, note, assigneeId, dueAt: due?.format('YYYY-MM-DD'), photos });
    } catch {
      err = 'Bộ nhớ trình duyệt đầy — bớt ảnh hoặc chụp ảnh nhỏ hơn';
    }
    if (err) { setError(err); return; }
    const closes = action === 'approve' || action === 'fail' || (action === 'submit' && !task.needReview);
    message.success(`${ACTION_LABEL[action]}: ${task.code}${closes && task.repeat ? ` — đã tạo lần tiếp theo (${REPEAT_LABEL[task.repeat].toLowerCase()})` : ''}`);
    onClose();
  };
  const addPhotos = async (files: FileList | null) => {
    if (!files) return;
    const room = MAX_PHOTOS - photos.length;
    try {
      const list = await Promise.all(Array.from(files).filter(f => f.type.startsWith('image/')).slice(0, room).map(f => compressImage(f)));
      setPhotos(p => [...p, ...list]);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const notePlaceholder: Partial<Record<Action, string>> = {
    submit: 'Kết quả / ghi chú bàn giao (không bắt buộc)',
    approve: 'Nhận xét (không bắt buộc)',
    return: 'Cần làm thêm gì? (bắt buộc)',
    due: 'Lý do đổi hạn (bắt buộc)',
    fail: 'Lý do không đạt (bắt buộc)',
    cancel: 'Lý do huỷ (bắt buộc)',
    reopen: 'Lý do mở lại (không bắt buộc)',
  };
  return (
    <Modal open zIndex={1100} onCancel={onClose} onOk={ok} okText={ACTION_LABEL[action]} cancelText="Đóng" title={`${ACTION_LABEL[action]} · ${task.code}`}
      okButtonProps={{ danger: action === 'fail' || action === 'cancel' }}>
      <div className="space-y-3">
        {action === 'submit' && <Alert type="info" showIcon message={task.needReview ? `Việc sẽ chuyển sang Chờ duyệt — ${memberName(task.creatorId)} sẽ duyệt kết quả.` : 'Việc không cần duyệt — sẽ hoàn thành ngay.'} />}
        {action === 'approve' && task.submittedAt && <Alert type="info" showIcon message={`Ngày hoàn thành tính theo ngày báo xong: ${fmtDate(task.submittedAt)}${task.dueAt && task.submittedAt > task.dueAt ? ' (muộn hạn)' : ''}.`} />}
        {action === 'reassign' && <Select className="w-full" showSearch optionFilterProp="label" placeholder="Giao cho" value={assigneeId} onChange={setAssigneeId} options={memberOptions(task.departmentId)} />}
        {action === 'due' && <DatePicker className="w-full" format="DD/MM/YYYY" value={due} onChange={setDue} placeholder="Hạn chót mới" disabledDate={d => !!task.startAt && d.isBefore(task.startAt, 'day')} />}
        {action === 'submit' && (
          <div>
            <div className="mb-1.5 text-sm">Ảnh kết quả {task.needProof ? <span className="text-red-500">(bắt buộc)</span> : <span className="text-slate-400">(không bắt buộc)</span>}</div>
            <div className="flex flex-wrap gap-2">
              {photos.map((src, i) => (
                <span key={i} className="relative">
                  <img src={src} alt={`Ảnh kết quả ${i + 1}`} className="h-20 w-20 rounded-md border border-slate-200 object-cover" />
                  <button onClick={() => setPhotos(p => p.filter((_, k) => k !== i))} aria-label="Bỏ ảnh"
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white"><X size={12} /></button>
                </span>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 text-xs text-slate-500 hover:border-[#3e91ab] hover:text-[#3e91ab]">
                  <Camera size={18} />Thêm ảnh
                  <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} />
                </label>
              )}
            </div>
          </div>
        )}
        {notePlaceholder[action] && <Input.TextArea autoFocus rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder={notePlaceholder[action]} status={error && NEEDS_NOTE.includes(action) && !note.trim() ? 'error' : undefined} />}
        {error && <div className="text-sm text-red-500">{error}</div>}
      </div>
    </Modal>
  );
}

export default function TaskDetail({ taskId, onClose, onOpen }: { taskId: string | null; onClose: () => void; onOpen: (id: string) => void }) {
  const tasks = useWorkStore(s => s.tasks);
  const updateTask = useWorkStore(s => s.updateTask);
  const comment = useWorkStore(s => s.comment);
  const [action, setAction] = useState<Action | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '' });
  const [text, setText] = useState('');
  const [addSub, setAddSub] = useState(false);
  const [checkText, setCheckText] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const check = useWorkStore(s => s.check);
  const addCheckItem = useWorkStore(s => s.addCheckItem);
  const t = tasks.find(x => x.id === taskId);
  if (!t) return <Drawer open={!!taskId} onClose={onClose} width={760}><Empty description="Không tìm thấy công việc" /></Drawer>;

  const actions = allowedActions(t, ME_ID);
  const primary = actions.filter(a => PRIMARY.includes(a));
  const more = actions.filter(a => !PRIMARY.includes(a));
  const subs = tasks.filter(s => s.parentId === t.id);
  const parent = tasks.find(p => p.id === t.parentId);
  const blocker = submitBlocker(t, tasks);
  const lastReturn = [...t.activity].reverse().find(a => a.type === 'return');
  const canEdit = actions.includes('edit');
  const checkable = canCheck(t, ME_ID);
  // Lịch sử chuỗi việc định kỳ: các lần đã đóng, bao nhiêu lần đúng hạn
  const series = t.seriesId ? tasks.filter(x => x.seriesId === t.seriesId && (x.status === 'done' || x.status === 'failed')) : [];
  const seriesOnTime = series.filter(x => bucketOf(x) === 'on_time').length;
  const run = (a: Action) => {
    if (a === 'edit') { setDraft({ name: t.name, description: t.description ?? '' }); setEditing(true); return; }
    if (a === 'submit' && blocker) { message.warning(blocker.includes('việc con') ? `${blocker} — hoàn thành hoặc huỷ việc con trước` : `${blocker} — tích đủ checklist trước khi báo xong`); return; }
    if (a === 'start') { useWorkStore.getState().act(t.id, 'start', { by: ME_ID }); message.success('Đã chuyển sang Đang làm'); return; }
    setAction(a);
  };

  return (
    <Drawer open={!!taskId} onClose={() => { setEditing(false); onClose(); }} width={780}
      title={<span className="flex flex-wrap items-center gap-2"><span className="font-mono text-sm text-slate-500">{t.code}</span><StatusTag t={t} /><PriorityTag t={t} /><RepeatTag t={t} /></span>}
      extra={(
        <span className="flex gap-2">
          {primary.map(a => <Button key={a} type={a === 'return' ? 'default' : 'primary'} onClick={() => run(a)}>{ACTION_LABEL[a]}</Button>)}
          {more.length > 0 && (
            <Dropdown trigger={['click']} menu={{ items: more.map(a => ({ key: a, label: ACTION_LABEL[a], danger: a === 'fail' || a === 'cancel' })), onClick: e => run(e.key as Action) }}>
              <Button icon={<MoreHorizontal size={16} />} />
            </Dropdown>
          )}
        </span>
      )}>
      {parent && <button onClick={() => onOpen(parent.id)} className="mb-2 text-xs text-[#3e91ab] hover:underline">↑ Việc cha: {parent.code} {parent.name}</button>}

      {t.status === 'review' && <Alert className="mb-3" type="warning" showIcon message={`Đã báo hoàn thành ngày ${fmtDate(t.submittedAt)} · chờ ${memberName(t.creatorId)} duyệt`} />}
      {t.status === 'doing' && lastReturn && <Alert className="mb-3" type="error" showIcon message={`${memberName(lastReturn.by)} trả lại ${dayjs(lastReturn.at).format('DD/MM HH:mm')}: ${lastReturn.text}`} />}
      {!t.assigneeId && !isClosed(t) && <Alert className="mb-3" type="warning" showIcon message="Việc chưa có người làm" action={actions.includes('reassign') && <Button size="small" onClick={() => setAction('reassign')}>Giao việc</Button>} />}

      {editing ? (
        <div className="mb-4 space-y-2">
          <Input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} className="!text-lg !font-semibold" />
          <Input.TextArea rows={4} value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} placeholder="Mô tả" />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditing(false)}>Huỷ</Button>
            <Button type="primary" disabled={!draft.name.trim()} onClick={() => { updateTask(t.id, { name: draft.name.trim(), description: draft.description.trim() }, ME_ID); setEditing(false); }}>Lưu</Button>
          </div>
        </div>
      ) : (
        <div className="mb-4">
          <h2 className={`text-xl font-semibold ${t.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{t.name}</h2>
          <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{t.description || <span className="text-slate-400">Chưa có mô tả</span>}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-x-6 rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-2 md:grid-cols-2">
        <Field label="Dự án">{projectOf(t.projectId)?.name}</Field>
        <Field label="Người giao"><Avatar id={t.creatorId} withName /></Field>
        <Field label="Người làm"><Avatar id={t.assigneeId} withName /></Field>
        <Field label="Theo dõi">
          {canEdit ? (
            <Select mode="multiple" size="small" className="w-full" variant="borderless" placeholder="Thêm người theo dõi" value={t.followerIds} optionFilterProp="label"
              options={memberOptions(t.departmentId)} maxTagCount="responsive" onChange={v => updateTask(t.id, { followerIds: v }, ME_ID)} />
          ) : (t.followerIds.map(memberName).join(', ') || '—')}
        </Field>
        <Field label="Bắt đầu">{fmtDate(t.startAt)}</Field>
        <Field label="Hạn chót"><DueText t={t} /></Field>
        {t.submittedAt && <Field label="Báo xong">{fmtDate(t.submittedAt)}</Field>}
        {t.doneAt && <Field label="Hoàn thành">{fmtDate(t.doneAt)}{t.dueAt && t.doneAt > t.dueAt && <span className="ml-1 text-amber-600">(muộn)</span>}</Field>}
        <Field label="Ưu tiên">
          <span className="flex flex-wrap gap-3">
            <Checkbox disabled={!canEdit} checked={t.important} onChange={e => updateTask(t.id, { important: e.target.checked }, ME_ID)}>Quan trọng</Checkbox>
            <Checkbox disabled={!canEdit} checked={t.urgent} onChange={e => updateTask(t.id, { urgent: e.target.checked }, ME_ID)}>Khẩn cấp</Checkbox>
          </span>
        </Field>
        <Field label="Duyệt kết quả">
          <Checkbox disabled={!canEdit || t.status === 'review'} checked={t.needReview} onChange={e => updateTask(t.id, { needReview: e.target.checked }, ME_ID)}>Người giao duyệt trước khi hoàn thành</Checkbox>
        </Field>
        <Field label="Ảnh kết quả">
          <Checkbox disabled={!canEdit || t.status === 'review'} checked={!!t.needProof} onChange={e => updateTask(t.id, { needProof: e.target.checked }, ME_ID)}>Báo xong phải kèm ảnh</Checkbox>
        </Field>
        {!t.parentId && (
          <Field label="Lặp lại">
            {canEdit ? (
              <Select size="small" className="w-36" value={t.repeat ?? ''} options={repeatOptions}
                onChange={v => updateTask(t.id, { repeat: (v || undefined) as Repeat | undefined }, ME_ID)} />
            ) : (t.repeat ? REPEAT_LABEL[t.repeat] : 'Không lặp')}
            {t.repeat && <div className="mt-1 text-xs text-slate-500">Đóng việc này sẽ tự tạo lần kế tiếp{series.length > 0 && ` · ${seriesOnTime}/${series.length} lần trước đúng hạn`}</div>}
          </Field>
        )}
      </div>

      {(t.checklist?.length || canEdit) ? (
        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="font-semibold">Checklist quy trình</h3><CheckCount t={t} />
          </div>
          {t.checklist?.length ? (
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
              {t.checklist.map(c => (
                <label key={c.id} className={`flex items-start gap-2.5 px-3 py-2 text-sm ${checkable ? 'cursor-pointer hover:bg-slate-50' : ''}`}>
                  <Checkbox className="mt-0.5" checked={!!c.done} disabled={!checkable} onChange={e => check(t.id, c.id, e.target.checked, ME_ID)} />
                  <span className="min-w-0 flex-1">
                    <span className={c.done ? 'text-slate-500' : ''}>{c.text}</span>
                    {c.done && c.by && <span className="block text-xs text-slate-400">{memberName(c.by)} · {dayjs(c.at).format('HH:mm DD/MM')}</span>}
                  </span>
                </label>
              ))}
            </div>
          ) : <div className="text-sm text-slate-400">Chưa có checklist</div>}
          {canEdit && !isClosed(t) && (
            <div className="mt-2 flex gap-2">
              <Input size="small" value={checkText} onChange={e => setCheckText(e.target.value)} placeholder="Thêm mục checklist…" maxLength={200}
                onPressEnter={() => { addCheckItem(t.id, checkText, ME_ID); setCheckText(''); }} />
              <Button size="small" icon={<Plus size={14} />} disabled={!checkText.trim()} onClick={() => { addCheckItem(t.id, checkText, ME_ID); setCheckText(''); }}>Thêm</Button>
            </div>
          )}
        </div>
      ) : null}

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold">Việc con <span className="font-normal text-slate-500">({subs.filter(isClosed).length}/{subs.length} xong)</span></h3>
          {!isClosed(t) && !t.parentId && (canEdit || t.assigneeId === ME_ID) && <Button size="small" icon={<Plus size={14} />} onClick={() => setAddSub(true)}>Thêm việc con</Button>}
        </div>
        {subs.length === 0 ? <div className="text-sm text-slate-400">Chưa có việc con</div> : (
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
            {subs.map(s => (
              <button key={s.id} onClick={() => onOpen(s.id)} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50">
                <span className="flex min-w-0 items-center gap-2"><Avatar id={s.assigneeId} size={22} /><span className={`truncate ${isClosed(s) ? 'text-slate-400 line-through' : ''}`}>{s.name}</span></span>
                <span className="flex shrink-0 items-center gap-2 text-xs"><DueText t={s} /><StatusTag t={s} /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6">
        <h3 className="mb-3 font-semibold">Lịch sử & trao đổi</h3>
        <Timeline items={t.activity.map(a => ({
          color: a.type === 'return' || a.type === 'fail' || a.type === 'cancel' ? 'red' : a.type === 'approve' ? 'green' : a.type === 'comment' ? 'gray' : 'blue',
          children: (
            <div className="text-sm">
              <b>{memberName(a.by)}</b> {ACTIVITY_TEXT[a.type]}{a.type === 'assign' && a.text ? <> <b>{memberName(a.text)}</b></> : null}
              <span className="ml-2 text-xs text-slate-400">{dayjs(a.at).format('DD/MM/YYYY HH:mm')}</span>
              {a.text && a.type !== 'assign' && <div className={`mt-1 whitespace-pre-line rounded-md px-3 py-2 ${a.type === 'comment' ? 'bg-slate-50' : 'bg-amber-50 text-amber-800'}`}>{a.text}</div>}
              {a.photos?.length ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {a.photos.map((src, i) => (
                    <button key={i} onClick={() => setPreview(src)} aria-label={`Xem ảnh kết quả ${i + 1}`}>
                      <img src={src} alt={`Ảnh kết quả ${i + 1}`} className="h-16 w-16 rounded-md border border-slate-200 object-cover" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ),
        }))} />
        <div className="flex items-end gap-2">
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} value={text} onChange={e => setText(e.target.value)} placeholder="Viết bình luận…"
            onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); comment(t.id, text, ME_ID); setText(''); } }} />
          <Button type="primary" icon={<Send size={14} />} disabled={!text.trim()} onClick={() => { comment(t.id, text, ME_ID); setText(''); }} />
        </div>
        {isOverdue(t) && t.assigneeId === ME_ID && <p className="mt-2 text-xs text-red-500">Việc đã quá hạn — báo hoàn thành hoặc trao đổi với {memberName(t.creatorId)} để xin đổi hạn.</p>}
      </div>

      <ActionModal key={`${t.id}-${action}`} task={t} action={action} onClose={() => setAction(null)} />
      <TaskForm open={addSub} onClose={() => setAddSub(false)} parent={t} />
      <Modal open={!!preview} footer={null} onCancel={() => setPreview(null)} zIndex={1200} width={720} title="Ảnh kết quả">
        {preview && <img src={preview} alt="Ảnh kết quả" className="w-full rounded-md" />}
      </Modal>
    </Drawer>
  );
}
